/**
 * Service Category V2 Mapping
 *
 * Builds the hierarchical service category taxonomy from RFI API data.
 *
 * Data shape expected (from API):
 *   rfi_id, rfi_name, rfi_grouping (can be "CRO;Technology"),
 *   is_parent_rfi, parent_rfi_id, is_new_rfi (boolean) or is_new (0/1)
 */

// ---------------------------------------------------------------------------
// 1. Build taxonomy from flat RFI data
// ---------------------------------------------------------------------------
function buildTaxonomyFromRfiData(rfiData) {
  if (!rfiData || rfiData.length === 0) return { list: [], groupOrder: [] };

  const groupOrderSet = new Set();
  const parentMap = new Map();

  rfiData.forEach((item) => {
    const itemGroups = (item.rfi_grouping || "").split(";").map((g) => g.trim()).filter(Boolean);
    itemGroups.forEach((g) => groupOrderSet.add(g));
    if (item.is_parent_rfi) {
      parentMap.set(item.rfi_id, item);
    }
  });

  // Foundation first, rest alphabetical
  const groupOrder = [...groupOrderSet].sort((a, b) => {
    if (a === "Foundation") return -1;
    if (b === "Foundation") return 1;
    return a.localeCompare(b);
  });
  const list = [];
  const seen = new Set();

  // Calculate depth by walking up the parent chain
  const getDepth = (item) => {
    let depth = 0;
    let current = item;
    while (current.parent_rfi_id && parentMap.has(current.parent_rfi_id)) {
      depth++;
      current = parentMap.get(current.parent_rfi_id);
    }
    return depth;
  };

  const makeEntry = (item, group) => {
    const hasParent = item.parent_rfi_id && parentMap.has(item.parent_rfi_id);
    return {
      name: item.rfi_name,
      group: group,
      depth: getDepth(item),
      isParent: item.is_parent_rfi || false,
      parentName: hasParent ? parentMap.get(item.parent_rfi_id)?.rfi_name || null : null,
      children: item.is_parent_rfi
        ? rfiData.filter((c) => c.parent_rfi_id === item.rfi_id).map((c) => c.rfi_name)
        : null,
      isNew: item.is_new_rfi === true || item.is_new === 1,
      rfiId: item.rfi_id,
    };
  };

  const addIfUnseen = (item, group) => {
    // Dedup by name within each group (handles duplicate "Investigator Site Budgets" etc.)
    const key = `${item.rfi_name}::${group}`;
    if (seen.has(key)) return;
    seen.add(key);
    list.push(makeEntry(item, group));
  };

  // Recursively add an item and its children (any depth)
  // Skip children whose name matches the parent (e.g. duplicate "Wearables (All Types)")
  const addWithChildren = (item, group, childrenByParent) => {
    addIfUnseen(item, group);
    if (childrenByParent.has(item.rfi_id)) {
      const children = childrenByParent.get(item.rfi_id)
        .filter((child) => child.rfi_name !== item.rfi_name);
      children.forEach((child) => {
        addWithChildren(child, group, childrenByParent);
      });
    }
  };

  groupOrder.forEach((group) => {
    const groupItems = rfiData.filter((item) => {
      const itemGroups = (item.rfi_grouping || "").split(";").map((g) => g.trim());
      return itemGroups.includes(group);
    });

    // Top-level: items without a parent (or whose parent isn't in this group)
    const topLevel = groupItems
      .filter((item) => !item.parent_rfi_id || !parentMap.has(item.parent_rfi_id))
      .sort((a, b) => a.rfi_name.localeCompare(b.rfi_name));

    // Children grouped by parent_rfi_id
    const childrenByParent = new Map();
    groupItems
      .filter((item) => item.parent_rfi_id && parentMap.has(item.parent_rfi_id))
      .sort((a, b) => a.rfi_name.localeCompare(b.rfi_name))
      .forEach((item) => {
        if (!childrenByParent.has(item.parent_rfi_id)) {
          childrenByParent.set(item.parent_rfi_id, []);
        }
        childrenByParent.get(item.parent_rfi_id).push(item);
      });

    // Add top-level items, recursively inserting all descendants
    topLevel.forEach((item) => {
      addWithChildren(item, group, childrenByParent);
    });
  });

  return { list, groupOrder };
}

// ---------------------------------------------------------------------------
// 2. Build lookup helpers from a taxonomy list
// ---------------------------------------------------------------------------
function buildLookups(list) {
  const scMeta = new Map();
  const scEntries = new Map();
  const newCategories = new Set();

  list.forEach((item) => {
    if (!scMeta.has(item.name)) scMeta.set(item.name, item);
    if (!scEntries.has(item.name)) scEntries.set(item.name, []);
    scEntries.get(item.name).push(item);
    if (item.isNew) newCategories.add(item.name);
  });

  return { scMeta, scEntries, newCategories };
}

// ---------------------------------------------------------------------------
// 3. Ancestor/descendant helpers (work with any lookups)
// ---------------------------------------------------------------------------
function _getAncestors(scName, scEntries) {
  const ancestors = new Set();
  const entries = scEntries.get(scName) || [];
  entries.forEach((entry) => {
    let current = entry;
    while (current?.parentName) {
      if (ancestors.has(current.parentName)) break;
      ancestors.add(current.parentName);
      const parentEntries = scEntries.get(current.parentName);
      current = parentEntries?.[0] || null;
    }
  });
  return [...ancestors];
}

function _getDescendants(scName, scEntries) {
  const result = new Set();
  const entries = scEntries.get(scName) || [];
  const stack = [];
  entries.forEach((entry) => {
    if (entry.children) stack.push(...entry.children);
  });
  while (stack.length) {
    const childName = stack.pop();
    if (result.has(childName)) continue;
    result.add(childName);
    const childEntries = scEntries.get(childName) || [];
    childEntries.forEach((ce) => {
      if (ce.children) stack.push(...ce.children);
    });
  }
  return [...result];
}

// ---------------------------------------------------------------------------
// 4. Cache — rebuilt when rfiData changes
// ---------------------------------------------------------------------------
let _cachedData = null;
let _cachedLookups = { scMeta: new Map(), scEntries: new Map(), newCategories: new Set() };

function ensureCache(rfiData) {
  if (rfiData === _cachedData) return;
  _cachedData = rfiData;
  const { list } = buildTaxonomyFromRfiData(rfiData);
  _cachedLookups = buildLookups(list);
}

// ---------------------------------------------------------------------------
// 5. Public API — all functions accept rfiData so they work with API data
// ---------------------------------------------------------------------------

/** Get metadata for a service category name. */
export function getV2Meta(scName, rfiData) {
  if (rfiData) ensureCache(rfiData);
  return _cachedLookups.scMeta.get(scName) ?? null;
}

/** Collect all ancestor names for a given SC. */
export function getAncestors(scName, rfiData) {
  if (rfiData) ensureCache(rfiData);
  return _getAncestors(scName, _cachedLookups.scEntries);
}

/** Collect all descendant names for a given SC. */
export function getDescendants(scName, rfiData) {
  if (rfiData) ensureCache(rfiData);
  return _getDescendants(scName, _cachedLookups.scEntries);
}

/** Returns true if the SC should show a "New" badge. */
export function isNewServiceCategory(name, rfiData) {
  if (rfiData) ensureCache(rfiData);
  if (!name) return false;
  return _cachedLookups.newCategories.has(name);
}

/**
 * Generate the grouped filter options from raw RFI API data.
 *
 * @param {Array} rfiData - raw RFI data from UseGetRfiListForFilters hook
 * @param {Object} opts
 */
export function generateV2ServiceCategoryOptions(
  rfiData = [],
  { dimInactive = false } = {}
) {
  const { list } = buildTaxonomyFromRfiData(rfiData);

  // Also update cache so getAncestors/getDescendants/isNew work
  _cachedData = rfiData;
  _cachedLookups = buildLookups(list);

  const options = [];
  let currentGroup = null;
  const groupMembers = {};

  list.forEach((item) => {
    if (item.group !== currentGroup) {
      currentGroup = item.group;
      if (!groupMembers[currentGroup]) groupMembers[currentGroup] = [];
      options.push({
        value: `__group__${item.group}`,
        label: item.group,
        isGroupHeader: true,
        checked: false,
        groupName: item.group,
      });
    }

    if (!groupMembers[currentGroup].includes(item.name)) {
      groupMembers[currentGroup].push(item.name);
    }

    options.push({
      value: item.name,
      label: item.name,
      rfiId: item.rfiId,
      checked: false,
      hasDocuments: true,
      groupName: item.group,
      depth: item.depth || 0,
      isParent: !!item.isParent,
      parentName: item.parentName || null,
      children: item.children || null,
    });
  });

  options.forEach((opt) => {
    if (opt.isGroupHeader) {
      opt.groupMembers = groupMembers[opt.groupName] || [];
    }
  });

  return options;
}

// Legacy exports for compatibility (not used but prevents import errors)
export const v2ServiceCategoryList = [];
export const GROUPING_ORDER = [];
